// ribbon.ts - 주차 리본의 순수 로직 (§4-1 시간축).
//
// 이 제품의 정체는 시간이다. SOP·T/O·샘플생산·검사가 언제인지가
// 화면이 답해야 할 질문의 절반이고, 형제 제품 jw-cms(원가·견적)에는
// 시간축 자체가 없다.
//
// KPI 카드는 "지금 몇 건"에 답하지만 "언제 몰려 있는가"에는 답하지
// 못한다. 리본은 그 질문 하나만 담당한다 - 다가올 N주를 가로로 깔고
// 각 주에 걸린 일정을 얹는다.
//
// 판정은 서버(`api/rules/status.py`)가 이미 내렸다. 여기서 상태를
// 다시 계산하지 않고 심각도 순서만 정한다.
import { addDays, isoDate, parseIsoDate } from "./dates";
import { SEVERITY, STATUS_CODES, type StatusCode } from "./status";
import { type WeekBucket, bucketOfIso, weeksInRange } from "./weeks";

/** 리본 한 칸에 얹히는 일정 하나. `getCalendar` 응답의 부분집합이다. */
export interface RibbonEvent {
  date: string;
  eventLabel: string;
  status: StatusCode;
  /**
   * 품명 · 개발품 id. `CalendarEvent` 가 이미 싣고 오므로 API 변경 없이
   * 「다가오는 일정」 목록이 "무엇의 무슨 일정인지"까지 말할 수 있다.
   * 선택 항목이다 - 리본의 집계(칸·막대)는 이 둘 없이도 성립한다.
   */
  partName?: string;
  devItemId?: string;
}

export interface RibbonCell {
  bucket: WeekBucket;
  /** 이번 주인가. 리본에서 유일하게 테두리가 강조되는 칸. */
  current: boolean;
  /** 이 주에 걸린 일정 전부 (날짜 오름차순). */
  events: RibbonEvent[];
  /** 칸을 대표하는 상태 - 가장 심각한 것. 비면 null. */
  peak: StatusCode | null;
  /**
   * 상태별 건수. `peak` 하나로는 "4건이 전부 지연인지, 지연 1 + 정상 3 인지"를
   * 말할 수 없다 - 한 주의 구성을 쌓아 보여주려면 이 분해가 필요하다.
   * 순서는 `STATUS_CODES`(§0-5) 를 그대로 쓴다.
   */
  counts: Record<StatusCode, number>;
}

/**
 * 두 상태 중 더 심각한 쪽.
 *
 * 심각도 순서는 `status.ts` 의 `SEVERITY`(§0-5) 를 그대로 쓴다. 여기에
 * 두 번째 순서표를 만들면 리본과 표가 다른 순서로 정렬되는 날이 온다.
 * 한 주에 지연과 정상이 같이 있으면 **지연이 이긴다** - 요약이 좋은
 * 소식을 앞세우면 요약을 신뢰할 수 없게 된다.
 */
export function peakStatus(a: StatusCode, b: StatusCode): StatusCode {
  return SEVERITY[a] >= SEVERITY[b] ? a : b;
}

/**
 * `from` 이 속한 주부터 `weeks` 개의 주차 칸을 만들고 일정을 배분한다.
 *
 * 범위 밖 일정은 버린다 - 리본은 "다가오는 것"만 말한다. 지난 것은
 * §11 지연·이슈 화면의 일이고, 두 화면이 같은 걸 말하면 어느 쪽이
 * 정본인지 알 수 없게 된다.
 */
export function buildRibbon(
  events: readonly RibbonEvent[],
  from: string,
  weeks: number
): RibbonCell[] {
  const start = parseIsoDate(from);
  // 주 단위로 끊기므로 마지막 주가 통째로 들어오도록 넉넉히 잡는다.
  const buckets = weeksInRange(start, addDays(start, weeks * 7 - 1)).slice(0, weeks);
  const todayKey = bucketOfIso(from).key;

  const byKey = new Map<string, RibbonEvent[]>();
  for (const e of events) {
    const k = bucketOfIso(e.date).key;
    const bucket = byKey.get(k);
    if (bucket) bucket.push(e);
    else byKey.set(k, [e]);
  }

  return buckets.map((bucket) => {
    const list = (byKey.get(bucket.key) ?? []).slice().sort((a, b) => a.date.localeCompare(b.date));
    const counts = Object.fromEntries(STATUS_CODES.map((c) => [c, 0])) as Record<StatusCode, number>;
    for (const e of list) counts[e.status] += 1;
    return {
      bucket,
      current: bucket.key === todayKey,
      events: list,
      peak: list.length === 0 ? null : list.map((e) => e.status).reduce(peakStatus),
      counts,
    };
  });
}

/**
 * 타일 한 칸에 들어갈 만큼 줄인 일정 이름.
 *
 * 마일스톤 라벨은 표·상세화면 기준으로 지어져 있어 8칸 리본에는 길다
 * (「금형 / 치공구 준비」는 130px 칸에서 통째로 잘린다). 슬래시 **양옆에
 * 공백이 있는** 복합 라벨만 앞부분을 남긴다 - `T/O` 처럼 공백 없는
 * 슬래시는 이름의 일부라 자르면 안 된다.
 *
 *   금형 / 치공구 준비 → 금형      검사 / 신뢰성 → 검사
 *   PPAP / 승인        → PPAP      T/O          → T/O (그대로)
 */
export function eventShortLabel(label: string): string {
  const head = label.split(" / ")[0].trim();
  return head || label;
}

/**
 * 그 주에 무엇이 걸렸는지, 타일에 들어갈 만큼만 (날짜 오름차순 최대 `max` 개).
 *
 * 숫자만으로는 "4건이 무엇인지"를 물어보러 칸을 눌러야 한다. 넘치는 건수는
 * 마지막 줄에 `+N` 으로 접는다 - 전체 목록은 타일의 `title` 속성이 갖는다.
 */
export function cellLabels(cell: RibbonCell, max: number): string[] {
  const shown = cell.events.slice(0, max).map((e) => eventShortLabel(e.eventLabel));
  const rest = cell.events.length - shown.length;
  return rest > 0 ? [...shown, `+${rest}`] : shown;
}

/**
 * 리본 전체에서 가장 먼저 오는 일정 `n` 건 (날짜 오름차순).
 *
 * 칸의 높이·색은 "언제 몰리나"에 답하지만, 실무자가 실제로 묻는 다음 질문은
 * "그래서 다음에 뭐가 오나"다. 칸을 열어보게 하지 않고 옆에 바로 답을 둔다.
 */
export function upcomingEvents(cells: readonly RibbonCell[], n: number): RibbonEvent[] {
  return cells.flatMap((c) => c.events).slice(0, n);
}

/** 리본이 덮는 마지막 날 (조회 범위의 `to`). */
export function ribbonEnd(from: string, weeks: number): string {
  return isoDate(addDays(parseIsoDate(from), weeks * 7 - 1));
}
